import ComboCounter from 'utils/ComboCounter';
import { MouseController } from 'app/chrome/mouse/MouseController';
import { UIController } from 'app/views/ui/UIController';
import { HomePromptsDeps } from './home.types';

/**
 * Prompt/guide side of the home section: the ComboCounter and all
 * drag-prompt / mouse-guide / next-section prompt state. Reads the current
 * x progress through its deps; UI/Mouse controller handles are injected from
 * HomeController.setControllers (mirrors WorkScene.setPixiController).
 */
export class HomePrompts {
	private _deps: HomePromptsDeps;

	private _uiController: UIController;
	private _mouseController: MouseController;

	private _counters: ComboCounter = new ComboCounter();
	private _hasGuidesActive: boolean = false;

	private _startDragPromptProgress: number = 0;

	private _spacesNextShown: boolean = false;
	private _systemsNextShown: boolean = false;
	private _spacesNextQueued: boolean = false;
	private _systemsNextQueued: boolean = false;
	private _spacesShown: boolean = false;
	private _systemsShown: boolean = false;

	constructor(deps: HomePromptsDeps) {
		this._deps = deps;
	}

	//Set controller depencies (set from HomeController.setControllers):
	public setControllers(ui: UIController, mouse: MouseController): void {
		this._uiController = ui;
		this._mouseController = mouse;
	}

	//Counters
	//-------------------------------------------------------------------------------------

	public tickCounters(): void {
		this._counters.tick();
	}

	//Prompt queueing
	//-------------------------------------------------------------------------------------

	/** Queue the drag prompt after the CHANGE_HOME_SCENE 'icon' scene change */
	public queueIconScenePrompt(): void {
		this._counters.add(() => {
			console.log('FIRED ICON CHANGE', this._deps.getProgress());
			if (this._deps.getProgress() < 0.02) {
				this._startDragPromptProgress = this._deps.getProgress();
				this._uiController.showDragPrompt(0);
				this._hasGuidesActive = true;
			}
		}, 6000);
	}

	/** Queue the delayed drag guide after the opening intro action completes */
	public queueOpeningDragGuide(): void {
		this._counters.add(
			() => {
				if (this._deps.getProgress() == 0) {
					this._startDragPromptProgress = this._deps.getProgress();
					this._uiController.showDragPrompt(0);
					this._hasGuidesActive = true;
					this._mouseController.guideFollowActive = true;

					this._counters.add(
						() => {
							this._mouseController.addArrowGuide('right');
						},
						500,
						0,
						'showMouseGuideArrow'
					);
				} else {
					this._deps.onPromptNoFollowCursor();
				}
			},
			3800,
			0,
			'showDragGuide'
		);
	}

	public showDragPrompt(): void {
		this._startDragPromptProgress = this._deps.getProgress();
		this._uiController.showDragPrompt(this._deps.getProgress());
		this._hasGuidesActive = true;
	}

	//Per-frame processing
	//-------------------------------------------------------------------------------------

	public processNextPrompts(): void {
		let diff = Math.abs(this._deps.getProgress() - this._startDragPromptProgress);

		if (this._hasGuidesActive && diff > 0.02) {
			this._clearDragGuide();
			this._mouseController.guideFollowActive = false;
		}

		return;

		if (this._spacesShown && this._systemsShown) {
			return;
		}

		let inScreens = this._deps.getProgress() > 0.32 && this._deps.getProgress() < 0.34;
		let inSpaces = this._deps.getProgress() > 0.66 && this._deps.getProgress() < 0.67;

		if ((!this._spacesNextQueued || !this._spacesNextShown) && inScreens) {
			if (!this._spacesNextQueued && !this._spacesNextShown) {
				this._spacesNextQueued = true;
				this._counters.add(
					() => {
						this._uiController.showNextPrompt('spaces');
						this._spacesNextQueued = false;
						this._spacesNextShown = true;
					},
					5000,
					0,
					'spaces-next'
				);
			}
		} else if ((this._spacesNextQueued || this._spacesNextShown) && !inScreens) {
			if (this._spacesNextQueued) {
				this._counters.clearById('spaces-next');
				this._spacesNextQueued = false;
			}

			if (this._spacesNextShown) {
				this._uiController.hideDragPrompt();
				this._spacesNextShown = false;
			}
		}

		if ((!this._systemsNextQueued || !this._systemsNextShown) && inSpaces) {
			if (!this._systemsNextQueued && !this._systemsNextShown) {
				this._systemsNextQueued = true;
				this._counters.add(
					() => {
						this._uiController.showNextPrompt('systems');
						this._systemsNextQueued = false;
						this._systemsNextShown = true;
					},
					5000,
					0,
					'systems-next'
				);
			}
		} else if ((this._systemsNextQueued || this._systemsNextShown) && !inSpaces) {
			if (this._systemsNextQueued) {
				this._counters.clearById('systems-next');
				this._systemsNextQueued = false;
			}

			if (this._systemsNextShown) {
				this._uiController.hideDragPrompt();
				this._systemsNextShown = false;
			}
		}

		if (this._deps.getProgress() > 0.66 && this._deps.getProgress() < 0.67) {
			this._spacesShown = true;
		}

		if (this._deps.getProgress() > 0.95) {
			this._systemsShown = true;
		}
	}

	//Clearing
	//-------------------------------------------------------------------------------------

	public clearOnWayToWork(): void {
		if (this._hasGuidesActive && this._deps.getProgress() < 0.05) {
			this._clearDragGuide();
		}

		if (this._spacesNextQueued) {
			this._counters.clearById('spaces-next');
			this._spacesNextQueued = false;
		}

		if (this._spacesNextShown) {
			this._uiController.hideDragPrompt();
			this._spacesNextShown = false;
		}

		if (this._systemsNextQueued) {
			this._counters.clearById('systems-next');
			this._systemsNextQueued = false;
		}

		if (this._systemsNextShown) {
			this._uiController.hideDragPrompt();
			this._systemsNextShown = false;
		}
	}

	private _clearDragGuide(): void {
		this._hasGuidesActive = false;
		this._counters.clearById('showDragGuide');
		this._counters.clearById('showDragGuideFooter');
		this._counters.clearById('showMouseGuideArrow');
		this._uiController.hideDragPrompt();
		this._mouseController.removeArrowGuides();
	}
}
